// /admin/social — la sezione Social. **Solo admin** (il server risponde 403 a tutti gli altri:
// backend/social_api.py). Otto schede, una per cosa che si fa davvero.
//
// Tre cose da sapere leggendo questo file:
// - qui non si decide niente. Ogni pulsante chiama il server e riporta quello che risponde: le regole
//   (chi può approvare, quando si pubblica, quali foto si possono usare) vivono nel backend, e questa
//   pagina non ne tiene una seconda copia che un giorno darà una risposta diversa;
// - la sospensione della pubblicazione automatica si mostra **in alto e in rosso**. È la regola di
//   sicurezza: se l'avviso «Post da approvare» è spento, nessuno vede l'anteprima, e un post che si
//   pubblica da solo dopo dodici ore sarebbe uscito senza che nessuno l'abbia mai guardato. Un
//   interruttore spento che cambia il comportamento di un'altra cosa, se non si dice, è una trappola;
// - il ✗ su una foto chiede **sempre** un motivo. Non per burocrazia: è quello che fa emergere da sé le
//   parole da escludere, e un ✗ senza motivo toglie la foto e non insegna niente.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import './social.css';

// Le schede in quattro gruppi, nell'ordine in cui si usano: prima quello che aspetta una nostra
// decisione, poi com'è andata, poi cosa ha fatto la squadra, poi le cose che si guardano di rado.
// Tredici voci in fila sono un elenco; quattro gruppi da tre sono un menu.
//
// `conta` dice da quale numero della fascia «Da fare» prende il contatore: una scheda senza
// contatore è una scheda dove non c'è niente da fare, e metterglielo a zero sarebbe rumore.
const GRUPPI = [
  ['Da decidere', [
    ['prossimi', 'Prossimi', 'post'],
    ['foto', 'Foto da approvare', 'foto'],
    ['proposte', 'Proposte della squadra', 'proposte'],
    ['commenti', 'Commenti', 'commenti'],
  ]],
  ['Risultati', [
    ['pubblicati', 'Pubblicati'],
    ['statistiche', 'Statistiche'],
  ]],
  ['Squadra', [
    ['decisioni', 'Decisioni della squadra'],
    ['concorrenza', 'Concorrenza'],
    ['ispirazioni', 'Ispirazioni'],
  ]],
  ['Archivio e sistema', [
    ['scartate', 'Scartate'],
    ['avvisi', 'Avvisi', 'avvisi'],
    ['calendario', 'Calendario'],
    ['nostre', 'Foto nostre'],
    ['impostazioni', 'Impostazioni'],
  ]],
];
const SCHEDE = GRUPPI.flatMap(([, voci]) => voci.map(([id, nome]) => [id, nome]));

// Lo stato del post → la classe del badge condiviso (`.tag` in staff.css). Prima questa pagina si
// disegnava i badge da sola, con i colori scritti a mano.
const COLORE_STATO = {
  bozza: 't-b', in_attesa: 't-o', approvato: 't-g', rifiutato: 't-r',
  in_pubblicazione: 't-w', pubblicato: 't-g', errore: 't-r',
};

const STATI = {
  bozza: 'Bozza', in_attesa: 'Da approvare', approvato: 'Approvato', rifiutato: 'Rifiutato',
  in_pubblicazione: 'In pubblicazione', pubblicato: 'Pubblicato', errore: 'Errore',
};

const quando = (iso) => {
  if (!iso) return '—';
  const d = new Date(/Z|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const avviso = (e) => toast.error(e?.response?.data?.detail || e?.message || 'Qualcosa non ha funzionato');

// ---------------------------------------------------------------- pezzi comuni

const Sospensione = ({ motivo }) => (motivo ? (
  <div className="so-allarme" data-testid="sospensione">
    <b>Pubblicazione automatica sospesa.</b> {motivo}
  </div>
) : null);

const Stato = ({ stato }) => (
  <span className={`tag ${COLORE_STATO[stato] || ''}`}>{STATI[stato] || stato || '—'}</span>
);

// ---------------------------------------------------------------- guardare le immagini

// Lo schermo intero. Si chiude con Esc, con la X o con un clic fuori dall'immagine, e con le frecce
// si passa alla successiva: sono i tre gesti che una persona prova da sola, e farne funzionare solo
// uno vuol dire che gli altri due sembrano la pagina rotta.
//
// `onGiudizio` c'è per la griglia delle fotografie: A approva, S scarta. Dove non serve non si passa,
// e i tasti non fanno niente invece di fare qualcosa di diverso.
const Pieno = ({ urls, indice, setIndice, onChiudi, onGiudizio, didascalia }) => {
  const quante = (urls || []).length;

  useEffect(() => {
    const tasto = (e) => {
      if (e.key === 'Escape') { onChiudi(); return; }
      if (e.key === 'ArrowRight') { setIndice((n) => (n + 1) % quante); return; }
      if (e.key === 'ArrowLeft') { setIndice((n) => (n - 1 + quante) % quante); return; }
      if (!onGiudizio) return;
      if (e.key === 'a' || e.key === 'A') onGiudizio('approva');
      if (e.key === 's' || e.key === 'S') onGiudizio('scarta');
    };
    window.addEventListener('keydown', tasto);
    return () => window.removeEventListener('keydown', tasto);
  }, [quante, onChiudi, setIndice, onGiudizio]);

  if (!quante) return null;
  return (
    <div className="so-pieno" data-testid="schermo-intero" role="dialog" aria-modal="true"
         onClick={(e) => { if (e.target === e.currentTarget) onChiudi(); }}>
      <div className="so-pieno-alto">
        <span>{quante > 1 ? `${indice + 1} di ${quante}` : ''}{didascalia ? ` · ${didascalia}` : ''}</span>
        <button type="button" className="btn sm" onClick={onChiudi} aria-label="Chiudi">✕ Chiudi</button>
      </div>
      <img src={urls[indice]} data-testid="immagine-intera"
           alt={quante > 1 ? `Immagine ${indice + 1} di ${quante}` : 'Immagine del post'} />
      <div className="so-pieno-basso">
        {quante > 1 && (
          <>
            <button type="button" className="so-freccia" aria-label="Precedente"
              onClick={() => setIndice((n) => (n - 1 + quante) % quante)}>‹</button>
            <button type="button" className="so-freccia" aria-label="Successiva"
              onClick={() => setIndice((n) => (n + 1) % quante)}>›</button>
          </>
        )}
        {onGiudizio && (
          <>
            <button type="button" className="btn w sm" onClick={() => onGiudizio('approva')}>✓ Approva (A)</button>
            <button type="button" className="btn rosso-vuoto sm" onClick={() => onGiudizio('scarta')}>✗ Scarta (S)</button>
          </>
        )}
      </div>
    </div>
  );
};

// L'anteprima grande, nel formato vero 4:5. Con più immagini è un carosello: frecce, pallini e il
// numero in alto, perché approvando si deve poter dire «la terza va spostata», non «quella con la
// casa». Col dito si scorre, con le frecce della tastiera anche.
const Visore = ({ urls, didascalia }) => {
  const elenco = urls || [];
  const [n, setN] = useState(0);
  const [pieno, setPieno] = useState(false);
  const [tocco, setTocco] = useState(null);
  const quante = elenco.length;
  const vai = useCallback((passo) => setN((v) => (v + passo + quante) % quante), [quante]);

  if (!quante) return <div className="so-visore" data-testid="senza-immagine" />;
  return (
    <div>
      <div
        className="so-visore"
        tabIndex={0}
        role="group"
        aria-label={quante > 1 ? `Carosello di ${quante} immagini` : 'Anteprima del post'}
        onKeyDown={(e) => {
          if (quante < 2) return;
          if (e.key === 'ArrowRight') { e.preventDefault(); vai(1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); vai(-1); }
        }}
        onTouchStart={(e) => setTocco(e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (tocco === null || quante < 2) return;
          const spostamento = e.changedTouches[0].clientX - tocco;
          if (Math.abs(spostamento) > 40) vai(spostamento < 0 ? 1 : -1);
          setTocco(null);
        }}
      >
        {/* un `alt` vero e non vuoto: l'anteprima **è** il contenuto della pagina, non una
            decorazione, e con `alt=""` per un lettore di schermo semplicemente non esiste */}
        <img src={elenco[n]} loading="lazy" data-testid="anteprima"
             alt={quante > 1 ? `Anteprima, immagine ${n + 1} di ${quante}` : 'Anteprima del post'}
             onClick={() => setPieno(true)} />
        {quante > 1 && (
          <>
            <button type="button" className="so-freccia prima" aria-label="Precedente"
              onClick={() => vai(-1)}>‹</button>
            <button type="button" className="so-freccia dopo" aria-label="Successiva"
              onClick={() => vai(1)}>›</button>
            <span className="punti-slide" data-testid="numero-slide">{n + 1}/{quante}</span>
          </>
        )}
      </div>
      {quante > 1 && (
        <div className="so-pallini">
          {elenco.map((u, i) => (
            <button type="button" key={u} className={i === n ? 'on' : ''}
              aria-label={`Vai all'immagine ${i + 1}`} onClick={() => setN(i)} />
          ))}
        </div>
      )}
      {pieno && (
        <Pieno urls={elenco} indice={n} setIndice={setN} didascalia={didascalia}
               onChiudi={() => setPieno(false)} />
      )}
    </div>
  );
};

// Il post dentro una cornice tipo feed. Serve a vedere **dove cade il taglio** della didascalia: su
// Instagram si interrompe dopo poche righe, e un post che mette la cosa importante al quarto
// capoverso la mette dove non la legge nessuno.
const CARATTERI_PRIMA_DI_ALTRO = 125;

const FeedInstagram = ({ urls, didascalia }) => {
  const [tutto, setTutto] = useState(false);
  const testo = String(didascalia || '');
  const taglia = !tutto && testo.length > CARATTERI_PRIMA_DI_ALTRO;
  return (
    <div className="so-feed" data-testid="come-instagram">
      <div className="so-feed-alto">
        <span className="tondo">MK</span>
        <b>mkconsulting.it</b>
      </div>
      {(urls || []).length > 0 && (
        <img src={urls[0]} loading="lazy" alt="Come si vedrebbe nel feed" />
      )}
      <div className="so-feed-testo">
        <b>mkconsulting.it</b>
        {taglia ? `${testo.slice(0, CARATTERI_PRIMA_DI_ALTRO)}… ` : testo}
        {taglia && <button type="button" onClick={() => setTutto(true)}>altro</button>}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Prossimi

// Quante volte si richiede lo stato dopo «Pubblica ora», e ogni quanto. 20 x 3 s = un minuto: oltre, il
// post resta in coda al giro del server e lo si vede ricaricando la pagina.
const ATTESE_PUBBLICAZIONE = 20;
const PAUSA_PUBBLICAZIONE = 3000;

// Le tre righe in russo, ucraino e romeno evidenziate finché Karolina le rilegge. Si marcano **qui**,
// dove si guardano, e non nel testo salvato: la didascalia che esce su Instagram non deve portare
// nessun segno di un nostro procedimento interno.
const Didascalia = ({ testo, righe, rilettura }) => {
  if (!rilettura || !(righe || []).length) {
    return <pre className="so-didascalia">{testo}</pre>;
  }
  const daRileggere = new Set(righe);
  return (
    <pre className="so-didascalia">
      {String(testo || '').split('\n').map((riga, n) => (
        <React.Fragment key={n}>
          {daRileggere.has(riga.trim())
            ? <mark className="rilettura" data-testid="da-rileggere" title="da far rileggere a Karolina">{riga}</mark>
            : riga}
          {'\n'}
        </React.Fragment>
      ))}
    </pre>
  );
};

const Prossimi = ({ dati, ricarica, slot }) => {
  const [apre, setApre] = useState(null);
  const [testo, setTesto] = useState('');
  const [orario, setOrario] = useState({});
  const [esce, setEsce] = useState(null);
  const [feed, setFeed] = useState(null);

  const agisci = async (id, strada, corpo) => {
    try {
      await axios({ method: corpo?._metodo || 'post', url: `${API}/social/post/${id}/${strada}`, data: corpo });
      toast.success('Fatto');
      setApre(null);
      await ricarica();
    } catch (e) { avviso(e); }
  };

  const rifiuta = async (id) => {
    const motivo = window.prompt('Perché si rifiuta? (obbligatorio, resta scritto nello storico)');
    if (!motivo || !motivo.trim()) return;
    await agisci(id, 'rifiuta', { motivo: motivo.trim() });
  };

  // Il server mette il post in coda e risponde subito: la pubblicazione vera (container, attesa di Meta,
  // media_publish) dura secondi e non sta dentro una richiesta web. Quindi qui si aspetta guardando: si
  // richiede lo stato finché non diventa «pubblicato» o «errore». Il tetto c'è perché un'attesa senza
  // fine è un modo per lasciare la pagina a girare per sempre; scaduto quello, il post resta comunque in
  // coda al giro del server, e lo si vede ricaricando.
  const pubblicaOra = async (id) => {
    if (!window.confirm('Il post uscirà subito su @mkconsulting.it. Confermi?')) return;
    setEsce(id);
    try {
      await axios.post(`${API}/social/post/${id}/pubblica-ora`);
      toast.success('In pubblicazione…');
      await ricarica();
      for (let n = 0; n < ATTESE_PUBBLICAZIONE; n += 1) {
        await new Promise((r) => setTimeout(r, PAUSA_PUBBLICAZIONE));
        const fresco = await ricarica(true);
        const post = (fresco?.voci || []).map((v) => v.post).find((p) => p && p.id === id);
        if (post?.stato === 'pubblicato') { toast.success('Pubblicato'); return; }
        if (post?.stato === 'errore') { toast.error(post.errore || 'La pubblicazione non è riuscita'); return; }
      }
    } catch (e) { avviso(e); } finally { setEsce(null); }
  };

  const creaTassi = async () => {
    try {
      const r = await axios.post(`${API}/social/bozza-tassi`);
      toast.success(r.data?.notificati ? 'Bozza creata, email mandata' : 'Bozza creata');
      await ricarica();
    } catch (e) { avviso(e); }
  };

  return (
    <div className="so-lista">
      <Sospensione motivo={dati?.sospensione} />
      <div className="so-azioni">
        <button type="button" className="btn sm" onClick={creaTassi}>Crea ora la bozza dei tassi</button>
      </div>
      {(dati?.voci || []).map((v) => (
        <div className={`card${v.post ? '' : ' sug'}${v.scaduto ? ' so-scaduto' : ''}`} key={`${v.slot_id}-${v.quando}-${v.post?.id || 'vuoto'}`}>
          <div className="ttl">
            <div>
              <b>{quando(v.quando)}</b>
              <span className="so-sub">{v.descrizione}</span>
            </div>
            {v.post
              ? <Stato stato={v.post.stato} />
              : <span className="tag">{v.vuoto_per_scelta ? 'Vuoto apposta' : 'Vuoto'}</span>}
          </div>

          {v.scaduto && (
            <p className="so-scaduto" data-testid="orario-passato">
              orario passato — pubblica ora o scegli un orario
            </p>
          )}

          {!v.post && (v.vuoto_per_scelta
            ? (
              <p className="muto" data-testid="vuoto-per-scelta">
                Lasciato vuoto apposta{v.motivo_vuoto ? ` — ${v.motivo_vuoto}` : ''}. Non è un buco:
                non comparirà negli avvisi.
              </p>
            )
            : <p className="vuoto">Nessuna bozza. Se resta così, lo slot viene saltato.</p>)}

          {v.post && (
            <>
              <div className="so-post">
                {feed === v.post.id
                  ? <FeedInstagram urls={v.post.immagini} didascalia={v.post.didascalia} />
                  : <Visore urls={v.post.immagini} didascalia={v.descrizione} />}

                <div>
                  {/* Rubrica, giorno e ora stanno già nell'intestazione della card, e lo stato
                      nel suo badge: qui si aggiunge solo quello che lì non c'è. */}
                  {v.lingua?.nome && (
                    <div className="kv">
                      <div><small>Lingua</small><b>{v.lingua.nome}</b></div>
                    </div>
                  )}

                  {apre === v.post.id ? (
                    <div className="campo-nota">
                      <textarea value={testo} onChange={(e) => setTesto(e.target.value)} rows={12} />
                      <div className="so-azioni">
                        <button type="button" className="btn p sm" onClick={() => agisci(v.post.id, 'didascalia', { didascalia: testo, _metodo: 'put' })}>Salva didascalia</button>
                        <button type="button" className="btn sm" onClick={() => setApre(null)}>Annulla</button>
                      </div>
                    </div>
                  ) : (
                    <Didascalia
                      testo={v.post.didascalia}
                      // le tre righe fisse delle lingue **più** quelle di questo post: la rubrica
                      // della domenica è scritta per metà in ucraino, e quelle righe lì non le
                      // conosce nessun elenco globale
                      righe={[...(dati?.righe_lingue || []), ...(v.post.righe_da_rileggere || [])]}
                      rilettura={dati?.rilettura} />
                  )}

                  {v.post.note && (
                    <details className="so-fonti" data-testid="fonti">
                      <summary>Fonti e note del Verificatore</summary>
                      <pre>{v.post.note}</pre>
                    </details>
                  )}
                  {v.post.foto?.autore && <p className="muto">Foto: {v.post.foto.autore} / Pixabay · tema «{v.post.foto.tema}»</p>}
                  {v.post.tema_ripiego && (
                    <p className="muto" data-testid="tema-ripiego">
                      Il tema di questa settimana era «{v.post.tema_ripiego}», ma non aveva foto approvate:
                      è stato usato un altro tema.
                    </p>
                  )}
                </div>
              </div>

              <div className="so-azioni">
                <button type="button" className="btn p" aria-label="Approva"
                  onClick={() => agisci(v.post.id, 'approva')} disabled={v.post.stato === 'approvato'}>✓ Approva</button>
                <button type="button" className="btn rosso-vuoto" aria-label="Rifiuta"
                  onClick={() => rifiuta(v.post.id)}>✗ Rifiuta</button>
                <button type="button" className="btn" onClick={() => { setApre(v.post.id); setTesto(v.post.didascalia || ''); }}>Modifica didascalia</button>
                <button type="button" className="btn" onClick={() => agisci(v.post.id, 'cambia-foto')}>Cambia foto</button>
                <button type="button" className="btn sm" aria-pressed={feed === v.post.id}
                  onClick={() => setFeed(feed === v.post.id ? null : v.post.id)}>
                  {feed === v.post.id ? 'Torna all\u2019anteprima' : 'Vedi come su Instagram'}
                </button>
                <select
                  value=""
                  onChange={(e) => e.target.value && agisci(v.post.id, 'slot', { slot_id: e.target.value, _metodo: 'put' })}
                  aria-label="Sposta su un altro slot"
                >
                  <option value="">Sposta su…</option>
                  {(slot || []).map((s) => <option key={s.id} value={s.id}>{s.descrizione}</option>)}
                </select>
              </div>

              {/* Uscita a mano: subito, o a un'ora scelta. «Pubblica ora» vale solo su un post approvato —
                  uno in attesa non l'ha ancora guardato nessuno, e il server lo rifiuta comunque. */}
              <div className="so-uscita">
                <button
                  type="button"
                  className="btn w"
                  disabled={v.post.stato !== 'approvato' || esce === v.post.id}
                  title={v.post.stato !== 'approvato' ? 'Prima va approvato' : ''}
                  onClick={() => pubblicaOra(v.post.id)}
                >
                  {esce === v.post.id ? 'In pubblicazione…' : 'Pubblica ora'}
                </button>
                <input
                  type="datetime-local"
                  value={orario[v.post.id] || ''}
                  onChange={(e) => setOrario({ ...orario, [v.post.id]: e.target.value })}
                  aria-label="Data e ora di uscita"
                />
                <button
                  type="button"
                  className="btn sm"
                  disabled={!orario[v.post.id]}
                  onClick={() => agisci(v.post.id, 'quando', { quando: orario[v.post.id], _metodo: 'put' })}
                >
                  Esci a quest'ora
                </button>
              </div>
              {v.post.storico?.length > 0 && (
                <details className="so-storico">
                  <summary>Storico ({v.post.storico.length})</summary>
                  <ul>{v.post.storico.map((r, i) => (
                    <li key={i}><b>{r.chi}</b> — {r.azione}{r.dettaglio ? `: ${r.dettaglio}` : ''} <i>{quando(r.quando)}</i></li>
                  ))}</ul>
                </details>
              )}
            </>
          )}
        </div>
      ))}
      {!(dati?.voci || []).length && <p className="vuoto">Nessuno slot nei prossimi giorni.</p>}
    </div>
  );
};

// ---------------------------------------------------------------- Pubblicati

const Pubblicati = ({ dati }) => (
  <div className="so-lista">
    {(dati?.post || []).map((p) => (
      <div className="card" key={p.id}>
        <div className="ttl">
          <div><b>{quando(p.pubblicato_il)}</b><span className="so-sub">{p.rubrica}</span></div>
          {p.permalink
            ? <a className="link" href={p.permalink} target="_blank" rel="noreferrer">Apri su Instagram</a>
            : <span className="muto">senza permalink</span>}
        </div>
        <Visore urls={p.immagini} />
        <pre className="so-didascalia">{p.didascalia}</pre>
      </div>
    ))}
    {!(dati?.post || []).length && <p className="vuoto">Ancora nessun post pubblicato.</p>}
  </div>
);

// ---------------------------------------------------------------- Calendario

// ---------------------------------------------------------------- Statistiche

const NOMI_METRICHE = {
  reach: 'copertura', views: 'visualizzazioni', saved: 'salvataggi', shares: 'condivisioni',
  likes: 'like', comments: 'commenti', total_interactions: 'interazioni',
  profile_visits: 'visite al profilo', follows: 'nuovi follower',
};

const Statistiche = ({ dati }) => {
  const post = dati?.post || [];
  const pubblico = dati?.pubblico || [];
  const finestre = dati?.finestre || ['24h', '72h', '7g'];
  const metriche = (dati?.metriche || []).filter((m) => NOMI_METRICHE[m]);

  return (
    <div className="so-lista">
      {!post.length && (
        <p className="vuoto">
          Ancora nessun numero. Le finestre si chiudono 24 ore, 72 ore e 7 giorni dopo ogni
          pubblicazione: il primo post ne avrà una fra un giorno.
        </p>
      )}
      {post.map((p) => (
        <div className="card" key={p.post_id}>
          <div className="ttl">
            <div>
              <b>{p.rubrica || 'post'}</b>
              <span className="so-sub">{quando(p.pubblicato_il)}</span>
            </div>
            {p.permalink && <a className="link" href={p.permalink} target="_blank" rel="noreferrer">su Instagram</a>}
          </div>
          <div className="so-tabella">
            <table>
              <thead>
                <tr><th>metrica</th>{finestre.map((f) => <th key={f}>{f}</th>)}</tr>
              </thead>
              <tbody>
                {metriche.map((m) => (
                  <tr key={m}>
                    <td>{NOMI_METRICHE[m]}</td>
                    {finestre.map((f) => (
                      <td key={f} data-testid={`${m}-${f}`}>
                        {p.finestre?.[f] ? (p.finestre[f].metriche?.[m] ?? '—') : '·'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {finestre.some((f) => p.finestre?.[f]?.ritardo_ore > 1) && (
            <p className="muto">
              Qualche finestra è stata letta in ritardo: i numeri non sono confrontabili con quelli
              degli altri post riga per riga.
            </p>
          )}
        </div>
      ))}

      <div className="card">
        <div className="ttl"><div><b>Il pubblico</b></div></div>
        {!pubblico.length && <p className="vuoto">Nessuna istantanea ancora.</p>}
        {pubblico.slice(0, 7).map((g) => (
          <p className="so-riga-pubblico" key={g.giorno}>
            <b>{g.giorno}</b> · follower {g.metriche?.follower_count ?? '—'} ·
            {' '}copertura {g.metriche?.reach ?? '—'} ·
            {' '}visite al profilo {g.metriche?.profile_views ?? '—'}
            {g.demografia_motivo === 'pochi_utenti' && (
              <span className="muto"> · città ed età non ancora disponibili: troppi pochi follower</span>
            )}
          </p>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Proposte della squadra

const Proposte = ({ dati, ricarica }) => {
  const [apre, setApre] = useState(null);

  const decidi = async (id, strada, corpo) => {
    try {
      await axios.post(`${API}/social/proposte/${id}/${strada}`, corpo || {});
      toast.success('Fatto');
      await ricarica();
    } catch (e) { avviso(e); }
  };

  const rifiuta = async (id) => {
    const motivo = window.prompt('Perché si rifiuta? (obbligatorio: è quello che la squadra legge la settimana dopo)');
    if (!motivo || !motivo.trim()) return;
    await decidi(id, 'rifiuta', { motivo: motivo.trim() });
  };

  const proposte = dati?.proposte || [];
  const attesa = proposte.filter((p) => p.stato === 'in_attesa');

  return (
    <div className="so-lista">
      {!proposte.length && (
        <p className="vuoto">Nessuna proposta. La squadra gira il lunedì mattina.</p>
      )}
      {!!attesa.length && (
        <p className="muto">
          {attesa.length} in attesa su un massimo di {dati?.massime_in_attesa || 20}.
        </p>
      )}
      {proposte.map((p) => (
        <div className={`card${p.stato === 'in_attesa' ? ' so-attesa' : ' so-chiusa'}`} key={p.id}>
          <div className="ttl">
            <div>
              <b>{p.titolo}</b>
              <span className="so-sub">{p.tipo} · da {p.da} · {quando(p.creata_il)}</span>
            </div>
            <Stato stato={p.stato} />
          </div>
          <p className="so-motivazione">{p.motivazione}</p>
          {!!Object.keys(p.numeri || {}).length && (
            <p className="so-numeri">
              {Object.entries(p.numeri).map(([k, v]) => (
                <span key={k}><b>{v}</b> {k.replace(/_/g, ' ')}</span>
              ))}
            </p>
          )}
          <button type="button" className="btn sm" onClick={() => setApre(apre === p.id ? null : p.id)}>
            {apre === p.id ? 'Nascondi i dettagli' : 'Cosa cambia'}
          </button>
          {apre === p.id && <pre className="so-didascalia">{JSON.stringify(p.dati, null, 2)}</pre>}
          {p.stato === 'in_attesa' && (
            <div className="so-azioni">
              <button type="button" className="btn sm" onClick={() => decidi(p.id, 'approva')}>✓ Approva e applica</button>
              <button type="button" className="btn sm" onClick={() => rifiuta(p.id)}>✗ Rifiuta</button>
            </div>
          )}
          {p.esito && <p className="muto">Esito: {p.esito}</p>}
          {p.motivo_rifiuto && <p className="muto">Rifiutata: {p.motivo_rifiuto}</p>}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------- Ispirazioni

const Ispirazioni = ({ dati, ricarica }) => {
  const [nota, setNota] = useState('');
  const [fonte, setFonte] = useState('');
  const [file, setFile] = useState(null);

  const carica = async () => {
    if (!nota.trim()) { toast.error('La nota serve: senza, fra un mese nessuno ricorda cosa colpiva'); return; }
    const corpo = new FormData();
    if (file) corpo.append('file', file);
    corpo.append('nota', nota.trim());
    corpo.append('fonte', fonte.trim());
    try {
      await axios.post(`${API}/social/ispirazioni`, corpo);
      toast.success('Salvata');
      setNota(''); setFonte(''); setFile(null);
      await ricarica();
    } catch (e) { avviso(e); }
  };

  const togli = async (id) => {
    if (!window.confirm('Togliere questa ispirazione?')) return;
    try { await axios.delete(`${API}/social/ispirazioni/${id}`); await ricarica(); } catch (e) { avviso(e); }
  };

  return (
    <div className="so-lista">
      <div className="card">
        <div className="ttl"><div><b>Aggiungi un'ispirazione</b></div></div>
        <p className="muto">
          La nota è la parte che conta: l'immagine da sola non insegna niente. Scrivi <i>perché</i>
          {' '}funziona — «il titolo dice un numero, non un aggettivo».
        </p>
        <textarea rows={3} value={nota} placeholder="Perché funziona…"
                  onChange={(e) => setNota(e.target.value)} />
        <input type="text" value={fonte} placeholder="Link o account (facoltativo)"
               onChange={(e) => setFonte(e.target.value)} />
        <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <div className="so-azioni"><button type="button" className="btn sm" onClick={carica}>Salva</button></div>
      </div>

      {(dati?.ispirazioni || []).map((i) => (
        <div className="card" key={i.id}>
          <div className="ttl">
            <div>
              <b>{i.account ? `@${i.account}` : 'ispirazione'}</b>
              <span className="so-sub">{quando(i.caricata_il)} · {i.caricata_da}</span>
            </div>
          </div>
          {i.immagine && <Visore urls={[i.immagine]} />}
          <p className="so-motivazione">{i.nota}</p>
          {i.fonte && <p className="muto">{i.fonte}</p>}
          <div className="so-azioni">
            <button type="button" className="btn sm" onClick={() => togli(i.id)}>Togli</button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------- Concorrenza

const Concorrenza = ({ dati, ricarica }) => {
  const [nuovo, setNuovo] = useState({ nome: '', account: '', sito: '', zona: '', note: '' });

  const aggiungi = async () => {
    try {
      await axios.post(`${API}/social/concorrenza`, nuovo);
      toast.success('Aggiunto');
      setNuovo({ nome: '', account: '', sito: '', zona: '', note: '' });
      await ricarica();
    } catch (e) { avviso(e); }
  };

  const togli = async (id) => {
    if (!window.confirm('Togliere questo concorrente? I suoi numeri restano.')) return;
    try { await axios.delete(`${API}/social/concorrenza/${id}`); await ricarica(); } catch (e) { avviso(e); }
  };

  const concorrenti = dati?.concorrenti || [];
  const numeri = dati?.numeri || [];

  return (
    <div className="so-lista">
      {!!dati?.riassunto?.testo && (
        <div className="card">
          <div className="ttl">
            <div><b>Cosa funziona per loro</b><span className="so-sub">{dati.riassunto.settimana}</span></div>
          </div>
          <pre className="so-didascalia">{dati.riassunto.testo}</pre>
        </div>
      )}

      <div className="card">
        <div className="ttl"><div><b>Aggiungi un concorrente</b></div></div>
        {['nome', 'account', 'sito', 'zona', 'note'].map((campo) => (
          <input key={campo} type="text" value={nuovo[campo]} placeholder={campo}
                 onChange={(e) => setNuovo({ ...nuovo, [campo]: e.target.value })} />
        ))}
        <div className="so-azioni"><button type="button" className="btn sm" onClick={aggiungi}>Aggiungi</button></div>
      </div>

      {!concorrenti.length && (
        <p className="vuoto">Nessun concorrente nell'elenco. Aggiungine qui sopra.</p>
      )}
      {concorrenti.map((c) => {
        const suoi = numeri.filter((n) => n.account === c.account).slice(0, 6);
        return (
          <div className="card" key={c.id}>
            <div className="ttl">
              <div>
                <b>{c.nome}</b>
                <span className="so-sub">@{c.account}{c.zona ? ` · ${c.zona}` : ''}</span>
              </div>
              <button type="button" className="btn sm" onClick={() => togli(c.id)}>Togli</button>
            </div>
            {c.note && <p className="so-motivazione">{c.note}</p>}
            {!suoi.length
              ? <p className="vuoto">Ancora nessun numero: li legge la squadra, una volta a settimana.</p>
              : (
                <div className="so-tabella">
                  <table>
                    <thead><tr><th>settimana</th><th>follower</th><th>post</th><th>reazione</th></tr></thead>
                    <tbody>
                      {suoi.map((n) => (
                        <tr key={n.settimana}>
                          <td>{n.settimana}</td>
                          <td>{n.followers_count ?? '—'}</td>
                          <td>{n.media_count ?? '—'}</td>
                          <td>{n.reazione_media != null ? `${n.reazione_media}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        );
      })}

      {!!(dati?.post || []).length && (
        <div className="card">
          <div className="ttl"><div><b>I loro post che vanno meglio</b></div></div>
          <p className="muto">
            Si tiene il link, mai una copia dell'immagine. La reazione è like più commenti ogni
            cento follower: delle loro insights non si può sapere niente.
          </p>
          {dati.post.slice(0, 12).map((p) => (
            <p className="so-riga-pubblico" key={p.permalink}>
              <b>@{p.account}</b> · {p.reazione != null ? `${p.reazione}%` : '—'} ·
              {' '}{p.like ?? '—'} like · {p.commenti ?? '—'} commenti ·{' '}
              <a className="link" href={p.permalink} target="_blank" rel="noreferrer">apri</a>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- Decisioni della squadra

const NOMI_DECISIONI = {
  foto_approvata: 'foto approvata', foto_scartata: 'foto scartata',
  tema_aggiunto: 'tema aggiunto', tema_ritirato: 'tema ritirato',
  esclusa_aggiunta: 'parola esclusa', concorrente_aggiunto: 'concorrente aggiunto',
  concorrente_tolto: 'concorrente tolto', orario_spostato: 'orario spostato',
};

const Decisioni = ({ dati, ricarica }) => {
  const annulla = async (id) => {
    if (!window.confirm('Annullare questa decisione? Le cose tornano come stavano.')) return;
    try {
      await axios.post(`${API}/social/decisioni/${id}/annulla`);
      toast.success('Annullata');
      await ricarica();
    } catch (e) { avviso(e); }
  };

  const cambiaRilettura = async (attiva) => {
    try {
      await axios({ method: 'put', url: `${API}/social/impostazioni/rilettura`, data: { attiva } });
      await ricarica();
    } catch (e) { avviso(e); }
  };

  const decisioni = dati?.decisioni || [];
  const attive = decisioni.filter((d) => !d.annullata_il);

  return (
    <div className="so-lista">
      <div className="card">
        <div className="ttl"><div><b>Le righe in russo, ucraino e romeno</b></div></div>
        <label className="so-interruttore">
          <input type="checkbox" checked={!!dati?.rilettura}
                 onChange={(e) => cambiaRilettura(e.target.checked)} />
          {' '}Karolina le rilegge prima di ogni pubblicazione
        </label>
        <p className="muto">
          Finché è acceso, nell'anteprima di ogni post quelle tre righe sono evidenziate. Si spegne
          quando vi fidate — e quel momento lo decidete voi, non il tempo che passa.
        </p>
      </div>

      {!decisioni.length && (
        <p className="muto">
          La squadra non ha ancora deciso niente da sola. Qui compariranno le foto approvate o
          scartate, i temi, i concorrenti e gli spostamenti di orario — ognuno con il suo perché.
        </p>
      )}
      {!!attive.length && <p className="muto">{attive.length} decisioni attive.</p>}

      {decisioni.map((d) => (
        <div className={`card${d.annullata_il ? ' so-chiusa' : ''}`} key={d.id}>
          <div className="ttl">
            <div>
              <b>{d.cosa}</b>
              <span className="so-sub">
                {NOMI_DECISIONI[d.tipo] || d.tipo} · {d.chi} · {quando(d.quando)}
              </span>
            </div>
            {d.annullata_il
              ? <span className="tag">annullata</span>
              : <button type="button" className="btn sm" onClick={() => annulla(d.id)}>Annulla</button>}
          </div>
          <p className="so-motivazione">{d.perche}</p>
          {d.annullata_il && (
            <p className="muto">
              Annullata da {d.annullata_da} il {quando(d.annullata_il)}
              {d.annullamento ? ` — ${d.annullamento}` : ''}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

const Calendario = ({ dati, ricarica }) => {
  const [nuovo, setNuovo] = useState({ giorno_settimana: 0, ora: '12:30', rubrica: '' });
  const quanti = (dati?.slot || []).length;

  const salva = async () => {
    try {
      await axios.post(`${API}/social/slot`, { ...nuovo, rubrica: nuovo.rubrica.trim() || null });
      toast.success('Slot aggiunto');
      await ricarica();
    } catch (e) { avviso(e); }
  };
  const togli = async (id) => {
    if (!window.confirm('Togliere questo slot? I post già fatti restano.')) return;
    try { await axios.delete(`${API}/social/slot/${id}`); await ricarica(); } catch (e) { avviso(e); }
  };
  const cambia = async (id, campi) => {
    try { await axios.put(`${API}/social/slot/${id}`, campi); await ricarica(); } catch (e) { avviso(e); }
  };

  return (
    <div className="so-cal">
      <p className="muto">
        {quanti} slot su un massimo di {dati?.massimo}. Gli orari sono in ora italiana: uno slot «lunedì 12:30»
        resta le 12:30 anche quando cambia l'ora legale.
      </p>
      <table className="so-tabella">
        <thead><tr><th>Giorno</th><th>Ora</th><th>Rubrica</th><th>Prossima volta</th><th /></tr></thead>
        <tbody>
          {(dati?.slot || []).map((s) => (
            <tr key={s.id}>
              <td>
                <select value={s.giorno_settimana} onChange={(e) => cambia(s.id, { giorno_settimana: Number(e.target.value) })} aria-label="Giorno">
                  {(dati.giorni || []).map((g, i) => <option key={g} value={i}>{g}</option>)}
                </select>
              </td>
              <td><input type="time" value={s.ora} onChange={(e) => cambia(s.id, { ora: e.target.value })} aria-label="Ora" /></td>
              <td>
                <input
                  defaultValue={s.rubrica || ''}
                  placeholder="libero"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (s.rubrica || '')) cambia(s.id, v ? { rubrica: v } : { togli_rubrica: true });
                  }}
                  aria-label="Rubrica"
                />
              </td>
              <td className="muto">{quando(s.prossima)}</td>
              <td><button className="rosso piccolo" onClick={() => togli(s.id)} disabled={quanti <= (dati?.minimo || 1)}>Togli</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="follow">
        <select value={nuovo.giorno_settimana} onChange={(e) => setNuovo({ ...nuovo, giorno_settimana: Number(e.target.value) })} aria-label="Giorno del nuovo slot">
          {(dati?.giorni || []).map((g, i) => <option key={g} value={i}>{g}</option>)}
        </select>
        <input type="time" value={nuovo.ora} onChange={(e) => setNuovo({ ...nuovo, ora: e.target.value })} aria-label="Ora del nuovo slot" />
        <input value={nuovo.rubrica} placeholder="rubrica (vuoto = libero)" onChange={(e) => setNuovo({ ...nuovo, rubrica: e.target.value })} aria-label="Rubrica del nuovo slot" />
        <button type="button" className="btn sm" onClick={salva} disabled={quanti >= (dati?.massimo || 14)}>Aggiungi slot</button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Foto da approvare

const Motivo = ({ motivi, onScegli, onChiudi }) => {
  const [altro, setAltro] = useState('');
  return (
    <div className="so-motivo" data-testid="scelta-motivo">
      <b>Perché si scarta?</b>
      <div className="so-motivi">
        {Object.entries(motivi || {}).map(([k, nome]) => (
          <button key={k} onClick={() => onScegli(k, k === 'altro' ? altro : '')}>{nome}</button>
        ))}
      </div>
      <input value={altro} onChange={(e) => setAltro(e.target.value)} placeholder="nota (facoltativa)" aria-label="Nota" />
      <button className="piano piccolo" onClick={onChiudi}>Annulla</button>
    </div>
  );
};

const FotoDaApprovare = ({ dati, ricarica }) => {
  const [chiede, setChiede] = useState(null);
  // quale foto è aperta a schermo intero: {tema, i, id}
  const [aperta, setAperta] = useState(null);

  const gruppoAperto = (dati?.gruppi || []).find((g) => g.tema.ricerca === aperta?.tema);
  const elencoAperto = gruppoAperto?.da_approvare || [];

  const decidi = async (id, strada, corpo, avanza = false) => {
    try {
      const r = await axios.post(`${API}/social/foto/${id}/${strada}`, corpo || {});
      if (r.data?.tema_da_riscrivere) toast.warning('Questo tema va riscritto: troppi rifiuti');
      setChiede(null);
      // Dopo un giudizio si passa **da soli** alla foto dopo. È tutta la differenza fra giudicarne
      // venti di fila e giudicarne tre: senza, a ogni foto si chiude, si cerca la prossima, si
      // riapre — e dopo la terza si smette.
      //
      // L'indice resta lo stesso perché la foto appena giudicata esce dall'elenco e quella dopo
      // scala al suo posto; se era l'ultima, si chiude.
      if (avanza && aperta) {
        const quante = elencoAperto.length - 1;
        if (quante <= 0) setAperta(null);
        else setAperta({ ...aperta, i: Math.min(aperta.i, quante - 1), id: null });
      }
      await ricarica();
    } catch (e) { avviso(e); }
  };

  const tutte = async (tema) => {
    try {
      const r = await axios.post(`${API}/social/foto/approva-tutte`, { tema });
      toast.success(`${r.data.approvate} foto approvate`);
      await ricarica();
    } catch (e) { avviso(e); }
  };

  const escludi = async (tema, parola) => {
    try { await axios.post(`${API}/social/temi/escluse`, { tema, parola }); toast.success(`«${parola}» esclusa da «${tema}»`); await ricarica(); } catch (e) { avviso(e); }
  };

  const temi = (dati?.gruppi || []).map((g) => g.tema.ricerca);

  return (
    <div className="so-foto">
      {/* il conteggio delle usabili per tema, tutto insieme e in cima: è la domanda che si fa
          arrivando qui («quale tema è magro?»), non una da cercare scheda per scheda */}
      <div className="so-scorta">
        {(dati?.gruppi || []).map((g) => (
          <span key={g.tema.ricerca}
                className={`punti${g.disponibili < g.soglia ? ' w' : ' ok'}`}
                data-testid={`usabili-${g.tema.ricerca}`}>
            {g.tema.ricerca}: {g.disponibili}
          </span>
        ))}
      </div>
      <CaricaNostre temi={temi} ricarica={ricarica} compatto />

      {(dati?.gruppi || []).map((g) => (
        <section key={g.tema.ricerca}>
          <div className="ttl">
            <div>
              <b>{g.tema.ricerca}</b>
              <span className="so-sub">soggetto: {g.tema.soggetto.join(', ')}{g.tema.escluse?.length ? ` · escluse: ${g.tema.escluse.join(', ')}` : ''}</span>
            </div>
            <span className={`punti${g.disponibili < g.soglia ? ' w' : ' ok'}`} data-testid={`disponibili-${g.tema.ricerca}`}>
              {g.disponibili} usabili (soglia {g.soglia})
            </span>
          </div>

          {g.suggerite?.parole?.length > 0 && (
            <div className="so-riquadro" data-testid="esclusioni-suggerite">
              <b>Esclusioni suggerite</b>
              <p className="muto">Parole che tornano nelle foto scartate di questo tema e quasi mai in quelle approvate. Nessuna si aggiunge da sola.</p>
              <ul>
                {g.suggerite.parole.map((p) => (
                  <li key={p.parola}>
                    <code>{p.parola}</code> — in {p.rifiutate} scartate, in {p.approvate} approvate su {p.su_approvate}
                    <button className="btn xs" onClick={() => escludi(g.tema.ricerca, p.parola)}>Aggiungi al tema</button>
                    <button className="piano piccolo" onClick={() => toast('Lasciata com\'è')}>Ignora</button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {g.suggerite?.tags_incompleti?.length > 0 && (
            <div className="so-riquadro male" data-testid="tags-incompleti">
              <b>Tags incompleti ({g.suggerite.tags_incompleti.length})</b>
              <p className="muto">
                Scartate per persone o veicoli, ma quelle parole nei tags non c'erano: sono i casi che nessun
                filtro di parole può prendere.
              </p>
              <ul>{g.suggerite.tags_incompleti.map((t) => <li key={t.id}><i>{t.motivo}</i> — <code>{t.tags}</code></li>)}</ul>
            </div>
          )}

          {g.in_scaricamento?.length > 0 && (
            <div className="so-riquadro" data-testid="in-scaricamento">
              <b>Foto approvate in attesa del file ({g.in_scaricamento.length})</b>
              <p className="muto">
                La decisione è salvata. Manca solo l'immagine grande: si riprova da sola ogni dieci minuti.
                Finché non c'è, la foto non viene usata per i post.
              </p>
              <div className="so-griglia">
                {g.in_scaricamento.map((f) => {
                  const fallito = f.scaricamento?.stato === 'fallito';
                  return (
                    <figure key={f.id}>
                      <img src={`/api/files/${f.miniatura_r2}`} alt="" loading="lazy" />
                      <figcaption>
                        <span className={fallito ? 'so-etichetta rotta' : 'so-etichetta'}>
                          {fallito ? 'download fallito' : 'in scaricamento'}
                        </span>
                        <br />tentativi: {f.scaricamento?.tentativi || 0} su {dati.tentativi_scaricamento}
                        {f.scaricamento?.ultimo_errore
                          ? <><br /><span className="muto">{f.scaricamento.ultimo_errore}</span></> : null}
                        <br /><i>{f.deciso_da} · {quando(f.deciso_il)}</i>
                      </figcaption>
                      {fallito && (
                        <div className="so-giudizio">
                          <button className="piano piccolo" onClick={async () => {
                            try {
                              await axios.post(`${API}/social/foto/${f.id}/riprova-scaricamento`);
                              toast.success('Riprovato');
                              await ricarica();
                            } catch (e) { avviso(e); }
                          }}>Riprova</button>
                        </div>
                      )}
                    </figure>
                  );
                })}
              </div>
            </div>
          )}

          {g.da_approvare.length > 0 && (
            <div className="so-azioni">
              <button type="button" className="btn sm" onClick={() => tutte(g.tema.ricerca)}>Approva tutte ({g.da_approvare.length})</button>
            </div>
          )}

          <div className="so-griglia">
            {g.da_approvare.map((f, i) => (
              <figure key={f.id} className={aperta?.id === f.id ? 'scelta' : undefined}>
                {/* `contain` e non `cover`: una foto si giudica guardandola **intera**. Un ritaglio
                    che nasconde il soggetto fa approvare una cosa diversa da quella che poi esce. */}
                <div className="so-scatto" onClick={() => setAperta({ tema: g.tema.ricerca, i, id: f.id })}>
                  <img src={`/api/files/${f.miniatura_r2}`} loading="lazy"
                       alt={f.tags ? `Foto: ${f.tags}` : 'Foto da giudicare'} />
                </div>
                <figcaption>
                  {f.tags}
                  <br /><i>{f.fonte === 'nostra' ? 'nostra' : 'Pixabay'}{f.autore ? ` · ${f.autore}` : ''}</i>
                </figcaption>
                {chiede === f.id
                  ? <Motivo motivi={dati.motivi} onChiudi={() => setChiede(null)} onScegli={(motivo, nota) => decidi(f.id, 'rifiuta', { motivo, nota })} />
                  : (
                    <div className="so-giudizio">
                      <button type="button" className="btn w sm" onClick={() => decidi(f.id, 'approva')} aria-label="Approva">✓ Tieni</button>
                      <button type="button" className="btn rosso-vuoto sm" onClick={() => setChiede(f.id)} aria-label="Scarta">✗ Scarta</button>
                    </div>
                  )}
              </figure>
            ))}
          </div>
          {!g.da_approvare.length && <p className="vuoto">Niente da decidere per questo tema.</p>}
        </section>
      ))}

      {aperta && elencoAperto.length > 0 && (
        <Pieno
          urls={elencoAperto.map((f) => `/api/files/${f.miniatura_r2}`)}
          indice={Math.min(aperta.i, elencoAperto.length - 1)}
          setIndice={(f) => setAperta((v) => ({
            ...v, i: typeof f === 'function' ? f(Math.min(v.i, elencoAperto.length - 1)) : f }))}
          didascalia={elencoAperto[Math.min(aperta.i, elencoAperto.length - 1)]?.tags}
          onChiudi={() => setAperta(null)}
          onGiudizio={(cosa) => {
            const foto = elencoAperto[Math.min(aperta.i, elencoAperto.length - 1)];
            if (!foto) return;
            if (cosa === 'approva') decidi(foto.id, 'approva', {}, true);
            // Scartare chiede sempre il motivo, anche da qui: un ✗ senza motivo toglie la foto e
            // non insegna niente. Quindi lo schermo intero si chiude e la scelta appare sotto la
            // foto, dov'è sempre stata.
            else { setAperta(null); setChiede(foto.id); }
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------- Scartate

const Scartate = ({ dati, ricarica, filtro, setFiltro }) => {
  const ripristina = async (id) => {
    try { await axios.post(`${API}/social/foto/${id}/ripristina`); toast.success('Torna fra quelle da decidere'); await ricarica(); } catch (e) { avviso(e); }
  };
  return (
    <div className="so-foto">
      <div className="follow">
        <input value={filtro.tema} placeholder="filtra per tema" onChange={(e) => setFiltro({ ...filtro, tema: e.target.value })} aria-label="Filtra per tema" />
        <select value={filtro.motivo} onChange={(e) => setFiltro({ ...filtro, motivo: e.target.value })} aria-label="Filtra per motivo">
          <option value="">tutti i motivi</option>
          {Object.entries(dati?.motivi || {}).map(([k, nome]) => <option key={k} value={k}>{nome}</option>)}
        </select>
      </div>
      <div className="so-griglia">
        {(dati?.foto || []).map((f) => (
          <figure key={f.id}>
            <img src={`/api/files/${f.miniatura_r2}`} alt="" loading="lazy" />
            <figcaption>
              <b>{dati.motivi?.[f.motivo] || f.motivo}</b>
              {f.nota ? <> — {f.nota}</> : null}
              <br />{f.tema}
              <br /><i>{f.deciso_da} · {quando(f.deciso_il)}</i>
              <br /><span className="muto">{f.tags}</span>
            </figcaption>
            <div className="so-giudizio"><button className="piano piccolo" onClick={() => ripristina(f.id)}>Ripristina</button></div>
          </figure>
        ))}
      </div>
      {!(dati?.foto || []).length && <p className="vuoto">Nessuna foto scartata.</p>}
    </div>
  );
};

// ---------------------------------------------------------------- Foto nostre

// Il caricamento delle foto nostre: **più file insieme**, con il tema scelto una volta sola.
//
// Una alla volta era il motivo per cui non se ne caricava nessuna: scaricare dal telefono venti
// fotografie dell'ufficio e poi reinserire le parole chiave venti volte è un lavoro che si rimanda.
// `accept="image/*"` e `multiple` fanno aprire direttamente il rullino sul telefono.
//
// Le foto nostre entrano **già approvate** e hanno la precedenza su Pixabay: non è una scorciatoia,
// è che le ha scelte una persona guardandole, che è esattamente quello che l'approvazione serve a
// ottenere per quelle di Pixabay.
const CaricaNostre = ({ temi, ricarica, compatto = false }) => {
  const [file, setFile] = useState([]);
  const [parole, setParole] = useState('');
  const [inCorso, setInCorso] = useState(false);

  const carica = async () => {
    if (!file.length) return toast.error('Scegli almeno una foto');
    if (!parole.trim()) return toast.error('Almeno una parola chiave: è così che la foto si abbina a un tema');
    setInCorso(true);
    let fatte = 0;
    try {
      // una alla volta e non tutte insieme: così se la quindicesima è troppo grande, le prime
      // quattordici sono già salvate e il messaggio dice quante ne sono passate
      for (const f of file) {
        const modulo = new FormData();
        modulo.append('file', f);
        modulo.append('parole_chiave', parole);
        await axios.post(`${API}/social/foto/nostre`, modulo);
        fatte += 1;
      }
      toast.success(fatte === 1 ? 'Caricata' : `${fatte} foto caricate`);
      setFile([]); setParole('');
      await ricarica();
    } catch (e) {
      if (fatte) toast.warning(`${fatte} caricate, poi qualcosa è andato storto`);
      avviso(e);
    } finally { setInCorso(false); }
  };

  return (
    <div className={compatto ? 'follow' : 'card'} data-testid="carica-nostre">
      {!compatto && <div className="ttl"><h2>Carica foto nostre</h2></div>}
      <div className={compatto ? undefined : 'follow'}>
        <input type="file" accept="image/*" multiple aria-label="Foto da caricare"
               onChange={(e) => setFile(Array.from(e.target.files || []))} />
        <input list="so-temi" value={parole} aria-label="Tema o parole chiave"
               placeholder="tema o parole chiave, separate da virgola"
               onChange={(e) => setParole(e.target.value)} />
        <datalist id="so-temi">
          {(temi || []).map((x) => <option key={x} value={x} />)}
        </datalist>
        <button type="button" className="btn p sm" onClick={carica} disabled={inCorso}>
          {inCorso ? 'Carico…' : `Carica${file.length > 1 ? ` (${file.length})` : ''}`}
        </button>
      </div>
      {!compatto && (
        <p className="muto">
          Entrano già approvate e hanno la precedenza su Pixabay: le ha scelte una persona
          guardandole. Le parole chiave servono ad abbinarle al soggetto di un tema.
        </p>
      )}
    </div>
  );
};

const Nostre = ({ dati, ricarica }) => {

  return (
    <div className="so-foto">
      <CaricaNostre temi={dati?.temi} ricarica={ricarica} />
      <div className="so-griglia">
        {(dati?.foto || []).map((f) => (
          <figure key={f.id}>
            <img src={`/api/files/${f.percorso}`} alt="" loading="lazy" />
            <figcaption>{(f.parole_chiave || []).join(', ')}<br /><i>{f.caricata_da} · {quando(f.caricata_il)}</i></figcaption>
            <div className="so-giudizio">
              <button className="rosso piccolo" onClick={async () => {
                if (!window.confirm('Togliere questa foto?')) return;
                try { await axios.delete(`${API}/social/foto/nostre/${f.id}`); await ricarica(); } catch (e) { avviso(e); }
              }}>Togli</button>
            </div>
          </figure>
        ))}
      </div>
      {!(dati?.foto || []).length && <p className="vuoto">Nessuna foto nostra caricata.</p>}
    </div>
  );
};

// ---------------------------------------------------------------- Avvisi

const Avvisi = ({ dati, ricarica }) => {
  const cambia = async (tipo, campi) => {
    try { await axios.put(`${API}/social/avvisi/${tipo}`, campi); await ricarica(); } catch (e) { avviso(e); }
  };
  return (
    <div className="stack">
      <Sospensione motivo={dati?.sospensione} />
      <p className="muto">
        Dalle {dati?.silenzio?.da}:00 alle {dati?.silenzio?.a}:00 gli avvisi non urgenti aspettano il mattino.
        I guasti passano sempre, ma non più di una volta ogni {dati?.ore_fra_due_guasti} ore per lo stesso problema.
      </p>
      {Object.entries(dati?.tipi || {}).map(([tipo, voce]) => (
        <div className="card" key={tipo}>
          <div className="ttl">
            <div>
              <b>{dati.titoli?.[tipo] || tipo}</b>
              <span className="so-sub">{dati.guasti?.[tipo] ? 'guasto: passa anche di notte' : 'rispetta la fascia di silenzio'}</span>
            </div>
            <label className="so-interruttore">
              <input type="checkbox" checked={!!voce.acceso} onChange={(e) => cambia(tipo, { acceso: e.target.checked })} />
              <span>{voce.acceso ? 'acceso' : 'spento'}</span>
            </label>
          </div>
          <input
            defaultValue={(voce.destinatari || []).join(', ')}
            placeholder="nessun destinatario"
            onBlur={(e) => {
              const lista = e.target.value.split(',').map((x) => x.trim()).filter(Boolean);
              if (lista.join(',') !== (voce.destinatari || []).join(',')) cambia(tipo, { destinatari: lista });
            }}
            aria-label={`Destinatari di ${dati.titoli?.[tipo] || tipo}`}
          />
        </div>
      ))}
      <h3>Ultimi avvisi</h3>
      <table className="so-tabella">
        <thead><tr><th>Quando</th><th>Tipo</th><th>Oggetto</th><th>A chi</th><th>Esito</th></tr></thead>
        <tbody>
          {(dati?.storico || []).map((r, i) => (
            <tr key={i} data-testid={r.tipo === 'email_non_inviata' ? 'email-non-inviata' : undefined}>
              <td className="muto">{quando(r.quando)}</td>
              <td>{dati.titoli?.[r.tipo] || r.tipo}</td>
              <td>{r.oggetto}</td>
              <td className="muto">{(r.destinatari || []).join(', ')}</td>
              {/* L'esito è una colonna e non un dettaglio nascosto: la domanda che si fa aprendo
                  questa tabella è «è arrivata?», e prima la risposta non c'era. */}
              <td>
                {r.email_id
                  ? <span className="tag t-g" title={`id Resend ${r.email_id}`}>arrivata</span>
                  : <span className="tag t-r" title={r.motivo || ''}>non partita</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!(dati?.storico || []).length && <p className="vuoto">Nessun avviso ancora mandato.</p>}
    </div>
  );
};

// ---------------------------------------------------------------- Impostazioni

const Impostazioni = ({ dati }) => (
  <div className="stack">
    <div className="card">
      <b>Instagram</b>
      <ul>
        <li>Collegamento: {dati?.instagram?.collegato ? 'attivo' : 'non configurato'}</li>
        <li>Account: {dati?.instagram?.id_account || '—'}</li>
        <li>Token in scadenza: {dati?.instagram?.token_scade_il ? `${quando(dati.instagram.token_scade_il)} (fra ${dati.instagram.giorni_alla_scadenza} giorni)` : '—'}</li>
        <li>Ultimo rinnovo: {quando(dati?.instagram?.ultimo_rinnovo)}</li>
      </ul>
      <p className="muto">Il token non si mostra e non esce mai da queste rotte: si dice se c'è e quando scade.</p>
    </div>
    <div className="card">
      <b>Automazione</b>
      <ul>
        <li>Interruttore SOCIAL_ATTIVO: {dati?.attivo ? 'acceso' : 'spento'}</li>
        <li>Chiave di servizio della fase 2: {dati?.chiave_di_servizio?.impostata ? 'impostata' : 'non impostata'}</li>
      </ul>
    </div>
    <div className="card">
      <b>Tassi</b>
      <ul>
        <li>{dati?.spread?.nota}</li>
        <li>Dati da: <code>{dati?.tassi?.indirizzo}</code></li>
        <li>Oltre {dati?.tassi?.giorni_freschezza} giorni la bozza non si crea</li>
      </ul>
    </div>
    <div className="card">
      <b>Stile delle grafiche</b>
      <p className="muto">
        I valori della firma MK. Si possono approvare combinazioni nuove senza toccare il codice, e ogni voce
        ha un minimo e un massimo: un valore fuori limite si scarta e resta quello di prima.
      </p>
      <table className="so-tabella">
        <thead><tr><th>Voce</th><th>Ora</th><th>Limiti</th></tr></thead>
        <tbody>
          {Object.entries(dati?.stile || {}).map(([k, v]) => (
            <tr key={k}>
              <td><code>{k}</code></td>
              <td>{String(v)}</td>
              <td className="muto">{(dati?.limiti_stile?.[k] || []).join(' – ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ---------------------------------------------------------------- la fascia «Da fare»

// Quello che aspetta **noi**, in cima, con il numero e il collegamento alla scheda. Se non c'è
// niente lo dice in una riga: una fascia vuota che resta lì a occupare spazio insegna a saltarla.
const DA_FARE = [
  ['post', 'Post da approvare', 'prossimi'],
  ['foto', 'Foto da giudicare', 'foto'],
  ['proposte', 'Proposte della squadra', 'proposte'],
  ['commenti', 'Commenti da gestire', 'commenti'],
  ['avvisi', 'Avvisi aperti', 'avvisi'],
];

const DaFare = ({ conti, vai }) => {
  const voci = DA_FARE.filter(([chiave]) => (conti?.[chiave] || 0) > 0);
  return (
    <div className="so-dafare" data-testid="da-fare">
      {voci.length === 0
        ? <p className="vuoto">Tutto in ordine: non aspetta niente.</p>
        : voci.map(([chiave, nome, scheda]) => (
          <button type="button" key={chiave} className="btn sm" onClick={() => vai(scheda)}>
            <b>{conti[chiave]}</b> {nome}
          </button>
        ))}
    </div>
  );
};

// ---------------------------------------------------------------- Commenti (in attesa di Meta)

const Commenti = ({ dati }) => (
  <div className="so-lista">
    {(dati?.commenti || []).length === 0 ? (
      <div className="card">
        <div className="ttl"><h2>Commenti</h2></div>
        <p className="vuoto" data-testid="commenti-in-attesa">
          In attesa del permesso Meta. Appena l'app ha
          «instagram_business_manage_comments», i commenti con una parola chiave compaiono qui con
          il post, la lingua e i pulsanti per rispondere.
        </p>
      </div>
    ) : (
      (dati.commenti || []).map((c) => (
        <div className="card" key={c.id}>
          <div className="ttl"><h2>@{c.username}</h2><span className="tag t-o">{c.parola}</span></div>
          <p>{c.testo}</p>
        </div>
      ))
    )}
  </div>
);

// ---------------------------------------------------------------- la pagina

const Social = () => {
  const [scheda, setScheda] = useState(() => (window.location.hash || '#prossimi').slice(1));
  const [dati, setDati] = useState({});
  const [slot, setSlot] = useState([]);
  const [filtro, setFiltro] = useState({ tema: '', motivo: '' });
  const [carica, setCarica] = useState(true);
  const [daFare, setDaFare] = useState({});

  const strade = useMemo(() => ({
    prossimi: '/social/prossimi',
    pubblicati: '/social/pubblicati',
    calendario: '/social/slot',
    foto: '/social/foto/da-approvare',
    scartate: '/social/foto/scartate',
    nostre: '/social/foto/nostre',
    statistiche: '/social/statistiche',
    proposte: '/social/proposte',
    ispirazioni: '/social/ispirazioni',
    concorrenza: '/social/concorrenza',
    decisioni: '/social/decisioni',
    avvisi: '/social/avvisi',
    impostazioni: '/social/impostazioni',
    // I commenti arrivano col permesso Meta: finché non c'è, la scheda è vuota e lo dice.
    commenti: '/social/commenti',
  }), []);

  // `silenzioso`: ricarica senza accendere lo spinner. Serve a chi sta aspettando un esito e si ricarica
  // da solo ogni pochi secondi — con lo spinner la lista sparirebbe a ogni giro, e con lei il componente
  // che sta aspettando. Torna i dati, così chi aspetta può guardarli senza passare da `dati`, che a quel
  // punto del giro è ancora quello di prima.
  const ricarica = useCallback(async (silenzioso = false) => {
    const strada = strade[scheda] || strade.prossimi;
    if (!silenzioso) setCarica(true);
    try {
      const parametri = scheda === 'scartate'
        ? { params: { tema: filtro.tema || undefined, motivo: filtro.motivo || undefined } }
        : {};
      const r = await axios.get(`${API}${strada}`, parametri);
      setDati(r.data);
      if (scheda === 'prossimi' && !slot.length) {
        const s = await axios.get(`${API}/social/slot`);
        setSlot(s.data.slot || []);
      }
      return r.data;
    } catch (e) { avviso(e); return null; } finally { if (!silenzioso) setCarica(false); }
  }, [scheda, strade, filtro.tema, filtro.motivo, slot.length]);

  useEffect(() => { ricarica(); }, [ricarica]);
  useEffect(() => { window.location.hash = scheda; }, [scheda]);

  // I conti della fascia si rileggono a ogni cambio di scheda: approvare un post deve far scendere
  // il numero in cima, o la fascia diventa una cosa che dice sempre la stessa bugia.
  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/social/da-fare`)
      .then((r) => { if (vivo) setDaFare(r.data || {}); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [scheda, dati]);

  const nome = (SCHEDE.find(([id]) => id === scheda) || [, 'Social'])[1];

  return (
    <div className="st wrap so">
      <div className="head r">
        <div>
          <div className="k">Social</div>
          <h1>@mkconsulting.it {daFare.totale ? <em>{daFare.totale} da vedere</em> : null}</h1>
          <p>Calendario, approvazione e fotografie dei post. Solo admin.</p>
        </div>
      </div>

      <DaFare conti={daFare} vai={setScheda} />

      {/* Su telefono dodici schede in una barra che scorre si trovano solo scorrendo, e scorrere per
          cercare è il contrario di un menu: sotto i 560 px diventano una tendina sola. */}
      <div className="so-tendina">
        <label className="muto" htmlFor="so-scheda">Scheda</label>
        <select id="so-scheda" value={scheda} onChange={(e) => setScheda(e.target.value)}>
          {GRUPPI.map(([gruppo, voci]) => (
            <optgroup key={gruppo} label={gruppo}>
              {voci.map(([id, titolo, conta]) => (
                <option key={id} value={id}>
                  {titolo}{conta && daFare[conta] ? ` (${daFare[conta]})` : ''}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div className="so-gruppi">
        {GRUPPI.map(([gruppo, voci]) => (
          <div className="so-gruppo" key={gruppo}>
            <div className="k">{gruppo}</div>
            <div className="tabs" role="tablist" aria-label={gruppo}>
              {voci.map(([id, titolo, conta]) => (
                <button type="button" key={id} role="tab" aria-selected={scheda === id}
                  className={scheda === id ? 'on' : undefined} onClick={() => setScheda(id)}>
                  {titolo}{conta && daFare[conta] ? <b>{daFare[conta]}</b> : null}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <h2 className="sr-only">{nome}</h2>
      {carica && <p className="vuoto">Carico…</p>}
      {!carica && scheda === 'prossimi' && <Prossimi dati={dati} ricarica={ricarica} slot={slot} />}
      {!carica && scheda === 'pubblicati' && <Pubblicati dati={dati} />}
      {!carica && scheda === 'calendario' && <Calendario dati={dati} ricarica={ricarica} />}
      {!carica && scheda === 'foto' && <FotoDaApprovare dati={dati} ricarica={ricarica} />}
      {!carica && scheda === 'scartate' && <Scartate dati={dati} ricarica={ricarica} filtro={filtro} setFiltro={setFiltro} />}
      {!carica && scheda === 'nostre' && <Nostre dati={dati} ricarica={ricarica} />}
      {!carica && scheda === 'statistiche' && <Statistiche dati={dati} />}
      {!carica && scheda === 'proposte' && <Proposte dati={dati} ricarica={ricarica} />}
      {!carica && scheda === 'ispirazioni' && <Ispirazioni dati={dati} ricarica={ricarica} />}
      {!carica && scheda === 'concorrenza' && <Concorrenza dati={dati} ricarica={ricarica} />}
      {!carica && scheda === 'decisioni' && <Decisioni dati={dati} ricarica={ricarica} />}
      {!carica && scheda === 'avvisi' && <Avvisi dati={dati} ricarica={ricarica} />}
      {!carica && scheda === 'commenti' && <Commenti dati={dati} />}
      {!carica && scheda === 'impostazioni' && <Impostazioni dati={dati} />}
    </div>
  );
};

export default Social;
